import React, { useState, useCallback, useEffect } from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";
import { BlacklistAPIPromiseClient } from "../../gen/witbooking/backoffice/blacklist/v1/blacklist_grpc_web_pb";
import {
  UpsertEngineBlacklistRequest,
  GetBlacklistedUsersRequest,
} from "../../gen/witbooking/backoffice/blacklist/v1/blacklist_pb";
import { useGrpcClient } from "../../lib/grpc";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { getPropertyRpt, useUserAuthInfo } from "../../lib/auth";
import { ForbiddenSection } from "../../components/authorization-verifier";

const messages = defineMessages({
  addEmail: {
    id: "emailBlacklist.addEmail",
    defaultMessage: "Add Email",
  },
  emailPlaceholder: {
    id: "emailBlacklist.emailPlaceholder",
    defaultMessage: "Enter an email address",
  },
  emailInvalid: {
    id: "emailBlacklist.emailInvalid",
    defaultMessage: "Invalid email format.",
  },
  emailAlreadyExists: {
    id: "emailBlacklist.emailAlreadyExists",
    defaultMessage: "This email is already on the blacklist.",
  },
  listLimitExceeded: {
    id: "emailBlacklist.listLimitExceeded",
    defaultMessage: "The list cannot exceed 50 emails.",
  },
  fetchError: {
    id: "emailBlacklist.fetchError",
    defaultMessage: "Failed to retrieve the blacklist.",
  },
  save: {
    id: "emailBlacklist.save",
    defaultMessage: "Save",
  },
  saveError: {
    id: "emailBlacklist.saveError",
    defaultMessage: "An error occurred while saving the blacklist.",
  },
  saveSuccess: {
    id: "emailBlacklist.saveSuccess",
    defaultMessage: "Blacklist saved successfully.",
  },
  remove: {
    id: "emailBlacklist.remove",
    defaultMessage: "Remove",
  },
  saving: {
    id: "emailBlacklist.saving",
    defaultMessage: "Saving...",
  },
  blacklistEmails: {
    id: "emailBlacklist.blacklistEmails",
    defaultMessage: "Emails in Blacklist",
  },
  enterEmail: {
    id: "emailBlacklist.enterEmail",
    defaultMessage: "Add Email Address",
  },
  removeEmailConfirm: {
    id: "emailBlacklist.removeEmailConfirm",
    defaultMessage: "Are you sure you want to remove this email?",
  },
});

const EmailBlacklistManager = ({ propertyTicker }) => {
  const userAuthInfo = useUserAuthInfo();
  const intl = useIntl();
  const [emails, setEmails] = useState<string[]>([]);
  const [itemInput, setItemInput] = useState<string>("");
  const [addError, setAddError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const grpcClient = useGrpcClient(BlacklistAPIPromiseClient);
  const keycloakAuthz = useKeycloakAuthz();

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const fetchBlacklist = useCallback(async () => {
    setLoading(true);
    setFetchError(null);

    const token = await getPropertyRpt(propertyTicker, keycloakAuthz);

    try {
      const request = new GetBlacklistedUsersRequest();
      const response = await grpcClient.getBlacklistedUsers(request, {
        Authorization: `Bearer ${token}`,
      });
      setEmails(response.getEmailsList());
    } catch (e) {
      console.error("Failed to fetch blacklist:", e);
      setFetchError(intl.formatMessage(messages.fetchError));
    }

    setLoading(false);
  }, [grpcClient, propertyTicker, keycloakAuthz, intl]);

  useEffect(() => {
    fetchBlacklist();
  }, [fetchBlacklist]);

  const handleAddItem = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAddError(null);

    if (emails.length >= 50) {
      setAddError(intl.formatMessage(messages.listLimitExceeded));
      return;
    }

    if (!itemInput.trim()) {
      setAddError(intl.formatMessage(messages.enterEmail));
      return;
    }

    if (!validateEmail(itemInput.trim())) {
      setAddError(intl.formatMessage(messages.emailInvalid));
      return;
    }

    if (emails.includes(itemInput.trim())) {
      setAddError(intl.formatMessage(messages.emailAlreadyExists));
      return;
    }

    setEmails([...emails, itemInput.trim()]);
    setItemInput("");
  };

  const handleRemoveItem = (item: string) => {
    setEmails(emails.filter(email => email !== item));
  };

  const saveBlacklist = useCallback(async () => {
    setLoading(true);
    setSaveError(null);
    setConfigSuccess(false);

    const request = new UpsertEngineBlacklistRequest();
    request.setEmailsList(emails);
    const token = await getPropertyRpt(propertyTicker, keycloakAuthz);

    try {
      await grpcClient.upsertEngineBlacklist(request, {
        Authorization: `Bearer ${token}`,
      });
      setConfigSuccess(true);
    } catch (e) {
      console.error("Failed to update blacklist:", e);
      setSaveError(intl.formatMessage(messages.saveError));
    }

    setLoading(false);
  }, [grpcClient, emails, propertyTicker, keycloakAuthz, intl]);

  if (!userAuthInfo) {
    return <div>Loading...</div>;
  }
  if (!userAuthInfo.isWitAdmin) {
    return <ForbiddenSection showMessage />;
  }

  return (
    <Card className="p-4 w-100">
      <h3 className="mb-3">{intl.formatMessage(messages.blacklistEmails)}</h3>

      {fetchError && (
        <Alert variant="danger" className="p-2 w-100 text-center">
          {fetchError}
        </Alert>
      )}

      <Form.Group controlId="email-input" className="mb-4">
        <Form.Label className="font-weight-bold">
          {intl.formatMessage(messages.enterEmail)}
        </Form.Label>
        <div className="d-flex">
          <Form.Control
            type="email"
            value={itemInput}
            onChange={e => setItemInput(e.target.value)}
            placeholder={intl.formatMessage(messages.emailPlaceholder)}
            className="flex-grow-1"
          />
          <Button variant="primary" onClick={handleAddItem} disabled={loading} className="ml-3">
            {intl.formatMessage(messages.addEmail)}
          </Button>
        </div>
        {addError && <Form.Text className="text-danger mt-2">{addError}</Form.Text>}
      </Form.Group>

      <Form.Group>
        <Form.Label className="font-weight-bold">
          {intl.formatMessage(messages.blacklistEmails)}
        </Form.Label>
        <ul className="list-group mb-4">
          {emails.map((email, index) => (
            <li
              key={index}
              className="list-group-item d-flex justify-content-between align-items-center">
              {email}
              <Button
                variant="danger"
                size="sm"
                className="text-white"
                onClick={() => handleRemoveItem(email)}
                disabled={loading}>
                {intl.formatMessage(messages.remove)}
              </Button>
            </li>
          ))}
        </ul>
      </Form.Group>

      <div className="d-flex justify-content-end">
        {loading && <span className="mr-3">{intl.formatMessage(messages.saving)}</span>}
        {saveError && (
          <Alert variant="danger" className="p-2 w-100 text-center">
            {saveError}
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success" className="p-2 w-100 text-center">
            {intl.formatMessage(messages.saveSuccess)}
          </Alert>
        )}
        <Button variant="primary" onClick={saveBlacklist} disabled={loading} className="ml-3">
          {intl.formatMessage(messages.save)}
        </Button>
      </div>
    </Card>
  );
};

export default EmailBlacklistManager;
